import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/base/Toast';

// ---------- Tipos ----------
interface Cliente {
  id: string;
  nombre: string;            // persona de contacto
  razon_social: string;      // empresa
  rfc: string;
  telefono: string;
  email: string;
  direccion: string;
}

interface ClienteForm {
  nombre: string;
  razon_social: string;
  rfc: string;
  telefono: string;
  email: string;
  direccion: string;
}

function formatMonto(monto: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(monto);
}

const emptyForm: ClienteForm = {
  nombre: '',
  razon_social: '',
  rfc: '',
  telefono: '',
  email: '',
  direccion: '',
};

// ---------- Helpers ----------
const API_URL = 'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (res.status === 204) return null;
  return res.json();
}

export default function ClientesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ClienteForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Cliente | null>(null);

  // Cargar clientes al montar
  const cargarClientes = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/clientes/');
      setClientes(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar clientes', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarClientes();
  }, []);

  // Filtrado local
  const filtered = useMemo(
    () =>
      clientes.filter(
        (c) =>
          (c.razon_social || '').toLowerCase().includes(search.toLowerCase()) ||
          (c.nombre || '').toLowerCase().includes(search.toLowerCase()) ||
          (c.rfc || '').toLowerCase().includes(search.toLowerCase()) ||
          (c.email || '').toLowerCase().includes(search.toLowerCase())
      ),
    [clientes, search]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (c: Cliente) => {
    setEditingId(c.id);
    setForm({
      nombre: c.nombre || '',
      razon_social: c.razon_social || '',
      rfc: c.rfc || '',
      telefono: c.telefono || '',
      email: c.email || '',
      direccion: c.direccion || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    // Validar al menos uno de los dos campos
    if (!form.nombre.trim() && !form.razon_social.trim()) {
      showToast('warning', 'Falta información', 'Ingresa al menos un nombre o razón social.');
      return;
    }
    try {
      if (editingId) {
        const updated = await fetchWithAuth(`/clientes/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        });
        setClientes((prev) => prev.map((c) => (c.id === editingId ? updated : c)));
        showToast('success', 'Cliente actualizado', `${form.razon_social || form.nombre} se ha modificado correctamente.`);
      } else {
        const nuevo = await fetchWithAuth('/clientes/', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        setClientes((prev) => [...prev, nuevo]);
        showToast('success', 'Cliente registrado', `${form.razon_social || form.nombre} se ha dado de alta correctamente.`);
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err: any) {
      showToast('error', 'Error al guardar', err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetchWithAuth(`/clientes/${deleteTarget.id}`, { method: 'DELETE' });
      setClientes((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      showToast('success', 'Cliente eliminado', `${deleteTarget.razon_social || deleteTarget.nombre} se ha eliminado del sistema.`);
      setDeleteTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar', err.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto">
        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">Clientes</h1>
            <p className="text-sm text-foreground-500 mt-0.5">{clientes.length} clientes registrados</p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1.5" />
            Nuevo cliente
          </button>
        </div>

        {/* Búsqueda */}
        <div className="mb-4">
          <div className="relative max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, razón social, RFC o email..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-user-star-line text-xl text-foreground-300" />
              </div>
              <p className="text-sm text-foreground-500">
                {search ? 'No se encontraron clientes con ese criterio.' : 'No hay clientes registrados.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase tracking-wider px-5 py-3">Razón Social</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase tracking-wider px-5 py-3">Nombre</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase tracking-wider px-5 py-3 hidden md:table-cell">RFC</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase tracking-wider px-5 py-3 hidden lg:table-cell">Contacto</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase tracking-wider px-5 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filtered.map((c) => (
                    <tr key={c.id} className="hover:bg-background-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary-600">{(c.razon_social || c.nombre).charAt(0)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-800 truncate">{c.razon_social || '—'}</p>
                            <p className="text-xs text-foreground-400 truncate">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-sm text-foreground-700">{c.nombre || '—'}</span>
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <span className="text-xs font-mono text-foreground-600">{c.rfc || '—'}</span>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <div>
                          <p className="text-xs text-foreground-600">{c.telefono || '—'}</p>
                          <p className="text-[11px] text-foreground-400 truncate max-w-[200px]">{c.direccion || '—'}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/dashboard/clientes/${c.id}`)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-100 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Ver obras">
                            <i className="ri-building-line text-sm" />
                          </button>
                          <button onClick={() => openEdit(c)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Editar">
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button onClick={() => setDeleteTarget(c)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Eliminar">
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal crear/editar */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar cliente' : 'Nuevo cliente'}
        size="md"
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${!form.nombre.trim() && !form.razon_social.trim()
                ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              disabled={!form.nombre.trim() && !form.razon_social.trim()}
            >
              {editingId ? 'Guardar cambios' : 'Registrar cliente'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Razón social *</label>
            <input
              type="text"
              value={form.razon_social}
              onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
              placeholder="Ej: Constructora Pacifico S.A."
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nombre de contacto *</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Ej: Juan Pérez García"
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">RFC</label>
              <input
                type="text"
                value={form.rfc}
                onChange={(e) => setForm({ ...form, rfc: e.target.value })}
                placeholder="Ej: CPA980512XYZ"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Teléfono</label>
              <input
                type="text"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="Ej: 55-1234-5678"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Ej: contacto@empresa.mx"
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Dirección</label>
            <input
              type="text"
              value={form.direccion}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              placeholder="Ej: Av. Reforma 250, CDMX"
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            />
          </div>
        </div>
      </Modal>

      {/* Confirmación eliminar */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar cliente"
        message={`¿Estás seguro de eliminar a "${deleteTarget?.razon_social || deleteTarget?.nombre}"? Esta acción no se puede deshacer.`}
        confirmLabel="Sí, eliminar"
        variant="danger"
      />
    </DashboardLayout>
  );
}